import { useEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

type AutoRefreshOpts = {
  /** Optional: only observe inside this element instead of whole document */
  root?: HTMLElement | null;
  /** Optional: pass a ref instead of an element; the hook will wait until .current is set */
  rootRef?: React.RefObject<HTMLElement | null>;
  /** Debounce ms to avoid spam-refresh; 100–200ms is usually good */
  debounceMs?: number;
  /** If provided, the hook will refresh only these ScrollTrigger instances */
  triggersRef?: React.RefObject<(ScrollTrigger | null)[] | null>;
  /** If false, the hook is a no-op */
  enabled?: boolean;
};

export function useScrollTriggerAutoRefresh({ root, rootRef, debounceMs = 150, triggersRef, enabled = true }: AutoRefreshOpts = {}) {
  useEffect(() => {
    // If explicitly disabled, do nothing
    if (!enabled) return;
    // If a ref was provided but isn't ready yet, do nothing — effect will re-run when ref.current changes
    if (rootRef && !rootRef.current) return;

    const scope: HTMLElement | Document = root ?? rootRef?.current ?? document;
    let rafId = 0;
    let tId: ReturnType<typeof setTimeout> | null = null;
    let refreshing = false;                // guard while a refresh is running
    let lastRefresh = 0;                   // timestamp of last refresh
    let windowStart = Date.now();
    let refreshCountInWindow = 0;          // simple rate limiter
    const MAX_REFRESHES_PER_SECOND = 4;    // tune as needed
    const WINDOW_MS = 1000;

    const scheduleRefresh = () => {
      // rate-limit / prevent re-entrant refresh loops
      const now = Date.now();
      if (refreshing) return;
      if (now - windowStart > WINDOW_MS) {
        windowStart = now;
        refreshCountInWindow = 0;
      }
      if (refreshCountInWindow >= MAX_REFRESHES_PER_SECOND) return;

      if (tId) clearTimeout(tId);
      tId = setTimeout(() => {
        // Use rAF to ensure layout has settled before measuring
        rafId = requestAnimationFrame(() => {
          refreshing = true;
          try {
            // If caller provided explicit triggers, refresh only those.
            const explicit = triggersRef?.current && triggersRef.current.length > 0;
            if (explicit) {
              triggersRef!.current!.forEach(st => { try { st?.refresh(); } catch (e) {} });
            } else if (scope instanceof HTMLElement) {
              // Scoped refresh: refresh triggers whose trigger/scroller live inside scope
              ScrollTrigger.getAll().forEach(st => {
                const trg = st.trigger as Element | undefined;
                const scroller = (st as any).scrollingElement as Element | undefined;
                const shouldRefresh =
                  (trg && (trg === scope || scope.contains(trg))) ||
                  (scroller && (scroller === scope || scope.contains(scroller)));
                if (shouldRefresh) st.refresh();
              });
            } else {
              // Global fallback
              ScrollTrigger.refresh();
            }
             lastRefresh = Date.now();
             refreshCountInWindow++;
           } finally {
             // allow a short time for observers to settle before clearing guard
            setTimeout(() => { refreshing = false; }, 80);
           }
         });
       }, debounceMs);
    };

    // 1) Watch size changes (page + section)
    const resizeObservers: ResizeObserver[] = [];
    const roTargets: Element[] = [];

    const addResizeObserver = (el: Element) => {
      const ro = new ResizeObserver(() => scheduleRefresh());
      ro.observe(el);
      resizeObservers.push(ro);
      roTargets.push(el);
    };

    // Only observe the global document element when our scope is the whole document.
    // If the caller passed a scoped element, observe only that element to avoid
    // causing page-wide refresh loops (which can make <body> "flash").
    if (scope instanceof Document) {
      // Prefer observing documentElement instead of body to reduce duplicate notifications.
      addResizeObserver(document.documentElement);
    } else if (scope instanceof HTMLElement) {
      addResizeObserver(scope);
    }

    // 2) DOM mutations (child adds, content swaps)
    const mo = new MutationObserver(mutations => {
      // Ignore attribute-only changes unless they’re likely to affect layout
      const relevant = mutations.some(m =>
        m.type === "childList" || (m.type === "attributes" && /style|class/.test(m.attributeName || ""))
      );
      if (relevant) scheduleRefresh();
    });
    // Observe the scoped element, or documentElement for whole-page observation.
    const moTarget = scope instanceof HTMLElement ? scope : document.documentElement;
    mo.observe(moTarget, { childList: true, subtree: true, attributes: true, attributeFilter: ["style", "class"] });

    // 3) Image loads inside scope
    const addImgListeners = (container: Element | Document) => {
      const imgs: HTMLImageElement[] = Array.from((container as Document | Element).querySelectorAll<HTMLImageElement>("img") || []);
      imgs.forEach((img: HTMLImageElement) => {
        if (img.complete) return;
        img.addEventListener("load", scheduleRefresh, { once: true });
        img.addEventListener("error", scheduleRefresh, { once: true });
      });
    };
    addImgListeners(scope);

    // 4) Font loading completion
    // When variable/web fonts finish, metrics can shift and change heights.
    if (document.fonts?.ready) {
      document.fonts.ready.then(scheduleRefresh).catch(() => {});
    }
    // Some browsers support events on FontFaceSet:
    // @ts-ignore
    document.fonts?.addEventListener?.("loadingdone", scheduleRefresh);

    // 5) CSS transitions that change layout (height/margins/etc.)
    const onTransitionEnd = (e: TransitionEvent) => {
      if (!e.propertyName) return;
      if (/height|width|top|bottom|left|right|margin|padding|transform/.test(e.propertyName)) {
        scheduleRefresh();
      }
    };
    (scope instanceof HTMLElement ? scope : document.body).addEventListener("transitionend", onTransitionEnd, true);

    // 6) Also refresh after the full page load just in case
    const onLoad = () => scheduleRefresh();
    window.addEventListener("load", onLoad);

    return () => {
      if (tId) clearTimeout(tId);
      cancelAnimationFrame(rafId);
      refreshing = false;
      lastRefresh = 0;
      resizeObservers.forEach(ro => ro.disconnect());
      mo.disconnect();
      (scope instanceof HTMLElement ? scope : document.body).removeEventListener("transitionend", onTransitionEnd, true);
      window.removeEventListener("load", onLoad);
    };
  // If a ref is provided, re-run when its current changes. Keep debounceMs and explicit root in deps too.
  }, [root, rootRef?.current, debounceMs, triggersRef]);
}
